import { ButtonDestructive, ButtonPrimary } from "@nova/component-btn"
import { GlobalNotificationStatus } from "@nova/component-global-notification"
import { OrganizationSettings } from "@nova/component-organization-settings"
import { useTranslation } from "@nova/use-translation"
import { organizationSettingsFields } from "features/forms/OrganizationSettingsFields"
import { PdfLogoManager } from "features/forms/PdfLogoManager"
import { MapPreview } from "features/map/MapPreview"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react"
import { showGlobalModal, snapshot } from "runlevel/actions"
import { setGlobalNotificationContext } from "user/profile/slice"
import {
    selectActiveWorkspace,
    selectAllWorkspaces,
    updateWorkspace,
} from "../../../../WorkspaceManagementSlice"
import { CRSSelector } from "@nova/component-crs-selector"
import { coordinateSystems } from "plugins/PDFv2/PDFAnnotations/PDFCoordinateNotations"
import { showConfirmationModal } from "features/content-overlay/thunks"
import { useWorkspacePermissions } from "../../../../useWorkspacePermissions"
import { WorkspaceTextInput } from "@nova/component-inline-text-input"
import classNames from "classnames"
import * as Icons from "@nova/component-icons"
import { Select } from "@nova/component-select"
import { WorkspaceConfirmationModalName } from "@nova/constants"
import {
    selectHasUnsavedConfigurationChanges,
    selectIsSavingChanges,
    selectIsTakingStartingLocationSnapshot,
    selectLocation,
    selectSettings,
    selectWorkspaceName,
    setConfigurationFromSource,
    setHasUnsavedChanges,
    setIsSavingChanges,
    setIsTakingStartingLocationSnapshot,
    setLocation,
    setSetting,
    setWorkspaceName,
} from "./slice"
import { MapSnapshotResult } from "@nova/component-map-snapshot"
import { resetMapToInitialRegion } from "features/map/control/slice"
import { useRouter } from "pages"
import {
    getGenericWorkspacePage,
    getGenericWorkspacePageHeader,
    getGenericSaveableWorkspacePageContent,
} from "panels/routing/WorkspaceRoutes"

export const ConfigurationSettingsPage = getGenericWorkspacePage({
    Header: getGenericWorkspacePageHeader({ title: "Configuration" }),
    Content: getGenericSaveableWorkspacePageContent({
        Content: ConfigurationSettingsContent,
        Footer: ConfigurationSettingsFooter,
    }),
})

function ConfigurationSettingsContent({
    setHasUnsavedChanges,
}: {
    setHasUnsavedChanges?: Dispatch<SetStateAction<boolean>>
}) {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const activeWorkspace = useAppSelector(selectActiveWorkspace())
    const { isEditor } = useWorkspacePermissions()
    const router = useRouter("/workspace/:workspaceId")

    const hasUnsavedChanges = useAppSelector(selectHasUnsavedConfigurationChanges)
    const localWorkspaceName = useAppSelector(selectWorkspaceName)
    const localLocation = useAppSelector(selectLocation)
    const localSettings = useAppSelector(selectSettings)
    const isTakingStartingLocationSnapshot = useAppSelector(selectIsTakingStartingLocationSnapshot)
    const [didComeFromStartingLocationSnapshot] = useState(isTakingStartingLocationSnapshot)
    useEffect(() => {
        if (isTakingStartingLocationSnapshot) {
            dispatch(setIsTakingStartingLocationSnapshot(false))
        }
    }, [dispatch, isTakingStartingLocationSnapshot])

    useEffect(() => {
        if (!activeWorkspace || didComeFromStartingLocationSnapshot) return
        dispatch(
            setConfigurationFromSource({
                workspaceName: activeWorkspace.name,
                location: activeWorkspace.location!,
                settings: activeWorkspace.settings!,
                hasUnsavedChanges: false,
            })
        )
    }, [activeWorkspace, didComeFromStartingLocationSnapshot, dispatch])

    useEffect(() => {
        setHasUnsavedChanges?.(hasUnsavedChanges)
    }, [hasUnsavedChanges, setHasUnsavedChanges])

    const onSnapshotConfirm = useCallback(
        (r: MapSnapshotResult) => {
            const zoom = r.zoom()
            const { latitude, longitude } = r.centerLatLng()
            dispatch(setLocation({ latitude, longitude, zoom }))
            router.navigate({ path: "/settings/configuration" })
        },
        [router, dispatch]
    )

    const onSnapshotCancel = useCallback(() => {
        router.navigate({ path: "/settings/configuration" })
    }, [router])

    const crs =
        (localSettings?.find((setting) => setting.name === "crs")?.value as string | undefined) ||
        "WGS 84"
    const notation =
        (localSettings?.find((setting) => setting.name === "notation")?.value as
            | string
            | undefined) || Object.keys(coordinateSystems[crs].notations)[0]
    useEffect(() => {
        if (!coordinateSystems[crs].notations[notation]) {
            const newNotation = Object.keys(coordinateSystems[crs].notations)[0]
            dispatch(setSetting({ name: "notation", value: newNotation }))
        }
    }, [crs, dispatch, notation])

    const headerClasses = "text-gray-100 mb-2"

    const pdfLogosSetting = localSettings?.find((setting) => setting.name === "pdfLogos")?.value
    const pdfLogosValue = typeof pdfLogosSetting === "string" ? pdfLogosSetting : "{}"

    return (
        <div className="text-gray-0 flex flex-col gap-6">
            <div className="flex gap-4">
                <small className="w-[12.5rem] xs">{t("Rename Workspace")}</small>
                <div className="-mt-0.5 w-[18.75rem]">
                    <h5 className="text-gray-100 mb-2">{t("Workspace Name")}</h5>
                    <WorkspaceTextInput
                        name="workspace-name"
                        value={localWorkspaceName ?? ""}
                        handleChange={(v) => dispatch(setWorkspaceName(v))}
                        disabled={!isEditor}
                    />
                </div>
            </div>
            <div className="w-full h-px bg-gray-700" />
            <div className="text-white flex gap-4">
                <small className="w-[12.5rem] xs">{t("General Settings")}</small>
                <div className="-mt-0.5 pb-12 pr-8">
                    <div className="flex gap-12">
                        <div className=" w-[16.563rem]">
                            <OrganizationSettings
                                formItems={organizationSettingsFields}
                                editorValues={localSettings || []}
                                onChange={(name, value) => dispatch(setSetting({ name, value }))}
                                disableAll={!isEditor}
                            />
                        </div>
                        <div>
                            <h5 className={headerClasses}>{t("Coordinate System")}</h5>
                            <CRSSelector
                                onSelect={(crs) => {
                                    if (crs) dispatch(setSetting({ name: "crs", value: crs.name }))
                                    else dispatch(setSetting({ name: "crs", value: "WGS 84" }))
                                }}
                                allCRSs={Object.values(coordinateSystems)}
                                className="!h-10 bg-gray-700 !border-none w-[21.875rem] rounded-lg !text-gray-0 !pl-2 !pr-1.5"
                                defaultCRSId={(crs as string) || "WGS 84"}
                                disabled={!isEditor}
                            />
                            <div>
                                <h5 className={classNames(headerClasses, "mt-4")}>
                                    {t("Coordinate Notation")}
                                </h5>
                                <Select<string>
                                    options={Object.values(coordinateSystems[crs].notations).map(
                                        (system) => ({
                                            label: system.label,
                                            value: system.id,
                                        })
                                    )}
                                    onChange={(sysId) =>
                                        dispatch(setSetting({ name: "notation", value: sysId }))
                                    }
                                    disabled={!isEditor}
                                    value={coordinateSystems[crs].notations[notation].id}
                                />
                            </div>
                            <h5 className={classNames(headerClasses, "mt-4")}>
                                {t("Starting Area")}
                            </h5>
                            <MapPreview
                                title="Select Starting Area"
                                onClick={() => {
                                    dispatch(resetMapToInitialRegion())
                                    dispatch(
                                        snapshot({
                                            onConfirm: onSnapshotConfirm,
                                            onCancel: onSnapshotCancel,
                                        })
                                    )
                                    dispatch(setIsTakingStartingLocationSnapshot(true))
                                    router.navigate({ path: "/settings/map-snapshot" })
                                    return Promise.resolve()
                                }}
                                viewport={localLocation}
                                className="!w-[21.875rem] !h-[12.875rem]"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full h-px bg-gray-700" />
            <PdfLogoManager
                name="pdfLogos"
                value={pdfLogosValue}
                onChange={(name, value) => dispatch(setSetting({ name, value }))}
            />
        </div>
    )
}

function ConfigurationSettingsFooter() {
    const dispatch = useAppDispatch()
    const { isEditor, isAdmin } = useWorkspacePermissions()
    const allWorkspaces = useAppSelector(selectAllWorkspaces())
    const activeWorkspace = useAppSelector(selectActiveWorkspace())
    const hasUnsavedChanges = useAppSelector(selectHasUnsavedConfigurationChanges)
    const isSavingChanges = useAppSelector(selectIsSavingChanges)
    const localWorkspaceName = useAppSelector(selectWorkspaceName)
    const localLocation = useAppSelector(selectLocation)
    const localSettings = useAppSelector(selectSettings)

    const submitSettings = useCallback(async () => {
        if (!activeWorkspace) return
        dispatch(setIsSavingChanges(true))
        await dispatch(
            updateWorkspace({
                id: activeWorkspace.id,
                updates: {
                    name: localWorkspaceName || "Workspace",
                    location: localLocation,
                    settings: localSettings,
                },
            })
        ).unwrap()
        dispatch(setIsSavingChanges(false))
        dispatch(setHasUnsavedChanges(false))
        dispatch(
            setGlobalNotificationContext({
                message: "Settings have been saved",
                timeoutInMs: 5000,
                status: GlobalNotificationStatus.success,
            })
        )
    }, [activeWorkspace, dispatch, localWorkspaceName, localLocation, localSettings])

    const showDeleteWorkspaceModal = useCallback(() => {
        if (!activeWorkspace) return
        dispatch(
            showConfirmationModal({
                type: WorkspaceConfirmationModalName.DeleteWorkspace,
                entityId: activeWorkspace.id,
                entityName: activeWorkspace.name,
            })
        )
    }, [dispatch, activeWorkspace])

    const showLeaveWorkspaceModal = useCallback(() => {
        if (!activeWorkspace) return
        dispatch(
            showConfirmationModal({
                type: WorkspaceConfirmationModalName.LeaveWorkspace,
                entityId: activeWorkspace.id,
                entityName: activeWorkspace.name,
            })
        )
        dispatch(showGlobalModal())
    }, [dispatch, activeWorkspace])

    return (
        <div className="w-full h-full flex justify-between items-center">
            <ButtonPrimary
                onClick={submitSettings}
                disabled={!isEditor || !hasUnsavedChanges || isSavingChanges}
                title="Save Changes"
            />
            {isAdmin ? (
                <ButtonDestructive
                    LeftIcon={Icons.Trash}
                    onClick={showDeleteWorkspaceModal}
                    disabled={!isEditor || allWorkspaces.length <= 1}
                    title="Delete Workspace"
                />
            ) : (
                <ButtonDestructive
                    RightIcon={Icons.Exit}
                    onClick={showLeaveWorkspaceModal}
                    disabled={allWorkspaces.length <= 1}
                    title="Leave Workspace"
                />
            )}
        </div>
    )
}
